import {
  BANK_ZIPCODE_LENGTH,
  getBankAddressMandatoryFrom
} from "../../stores/drawRequest/drawRequest.constants";

// The user-fillable half of the bank address. Country is deliberately absent:
// it is fixed to USA and read-only, so flagging it would raise an error the user
// has no way to clear.
const BANK_ADDRESS_FIELDS = ["city", "state", "zipcode"];

// The draw's creation instant, which the cut-off is measured against.
// draw-service gates on the draw's own createdAt; requestedDate is the only
// draw-level timestamp the loan-details response currently carries, so it stands
// in until the backend exposes createdAt — at which point the first two branches
// pick it up with no further change here.
const getDrawCreatedAt = (drawObj: any): string =>
  drawObj?.createdAt || drawObj?.createdDate || drawObj?.requestedDate || "";

// Mirrors isBankAddressMandatoryForDraw in draw-service: same cut-off, same >=
// comparison, and the same rule that an absent or unparseable timestamp counts as
// NOT mandatory — such a draw cannot be placed relative to the cut-off, and
// wrongly blocking a save is worse than letting it through, since the backend
// enforces this too.
const isBankAddressMandatory = (drawObj: any): boolean => {
  const created = getDrawCreatedAt(drawObj);
  if (!created) return false;
  const createdAt = new Date(created).getTime();
  if (Number.isNaN(createdAt)) return false;
  return createdAt >= getBankAddressMandatoryFrom().getTime();
};

export const countDrawErrors = (
  drawObj: any,
  selectedBankDetails: any,
  // The routing value visible in the edit input (decrypted plaintext / freshly typed).
  // Used for the 9-digit rule instead of selectedBankDetails.routing, which can be a
  // masked last-4 carried over from a previous draw and would falsely fail validation.
  visibleRouting?: string
) => {
  let loanerrorCount = 0;
  const { drawAmountDetails } = drawObj;
  const fieldsToIgnore = [
    "drawId", // drawId comes from route (URL), not always on draw object from API
    "drawAmountDetails",
    "balanceToFinish",
    "originatorId",
    "propertyDetailsOptions",
    "drawType",
    "loanStatus",
    "finalApproval",
    "finalApprovalNotRequired",
    "originalMaturityDate", // todo:remove these below fields later
    "maturityDate",
    "currentMaturityDate",
    "nextPaymentDueDate",
    "gracePeriod",
    "payOffRequestDate",
    "availableFinancedBudgetAmount",
    "drawType",
    "loanExcelJson",
    "originatorName",
    "servicerId",
    "servicerName",
    "borrowerAddress",
    "requestedDate",
    "lastUpdatedDate",
    "createdAt",
    "documentTaggingStatus",
    "budgetReconcileStatus",
    "inspectionReviewStatus",
    "budgetReviewStatus",
    "prePurchaseDrawAmount",
    "brokerName",
    "originatingEntity",
    "sellerName",
    "fundingChannel",
    "fpiFlag",
    "repeatBorrower",
    "repeatBankDetailsDrawList",
    "losDrawId",
    "drawAmountType"
  ];

  Object.keys(drawObj).forEach((field) => {
    if (fieldsToIgnore.includes(field)) return;

    // Special handling for propertyDetails: require at least one property selected.
    if (field === "propertyDetails") {
      const props = drawObj.propertyDetails;
      if (!Array.isArray(props) || props.length === 0) {
        console.error("field Error:", field);
        loanerrorCount++;
      }
      return;
    }

    if (!drawObj[field]) {
      console.error("field Error:", field);
      loanerrorCount++;
    }
  });

  Object.keys(drawAmountDetails).forEach((field) => {
    if (["edited", "stage", "comments"].includes(field)) return;
    let isZeroNetFunds = false;
    if (field === "netFundsToBorrower") {
      isZeroNetFunds = drawAmountDetails[field] === "0.00";
    }

    if (
      (!drawAmountDetails[field] && drawAmountDetails[field] !== 0) ||
      isZeroNetFunds
    ) {
      console.error("field Error:", field);
      loanerrorCount++;
    }
  });

  if (selectedBankDetails) {
    // Keyed on the draw's age, not on the values present: a post-cut-off draw is
    // enforced even if every address field has been cleared.
    const addressMandatory = isBankAddressMandatory(drawObj);
    Object.keys(selectedBankDetails).forEach((field) => {
      if (
        [
          "netFundsToBorrower",
          "edited",
          "bankDetailId",
          "borrowerEntityName",
          "updatedBy",
          "needsDrawIdDecrypt",
          "accountNumberEncryptionId",
          "routingEncryptionId",
          "accountNameEncryptionId",
          "isEncrypted",
          "losBankDetailId",
          "country"
        ].includes(field)
      )
        return;
      if (BANK_ADDRESS_FIELDS.includes(field) && !addressMandatory) return;
      if (!selectedBankDetails[field]) {
        console.error("field Error:", field);
        loanerrorCount++;
        return;
      }
      if (field === "zipcode") {
        const zip = String(selectedBankDetails[field] ?? "");
        if (zip.length !== BANK_ZIPCODE_LENGTH) {
          console.error("field Error:", field);
          loanerrorCount += 1;
        }
      }
      if (field === "routing") {
        // Validate the value the user actually sees in the input. When visibleRouting
        // is empty the field still shows the stored (masked) value — whose real routing
        // is a valid 9-digit number — so the presence check above is enough and we don't
        // flag the 9-digit rule here.
        const routingToCheck = String(visibleRouting ?? "");
        if (routingToCheck && routingToCheck.length !== 9) {
          console.error("field Error:", field);
          loanerrorCount++;
        }
      }
    });
  } else {
    loanerrorCount += 4;
  }
  return loanerrorCount;
};